import { useEffect, useState } from 'react';

import {
  DEFAULT_REST_SOUND_ID,
  isRestSoundId,
  type RestSoundId,
} from '@jackedlogs/shared';

// web-rnw twin of ./restSoundPreference.ts (see that file's header) — same
// keys/shape, localStorage instead of AsyncStorage. The vibration toggle is
// meaningless on the DOM (no navigator.vibrate-backed haptics in practice
// across desktop browsers), but the preference still round-trips so a
// setting made on mobile and later read here (once Profile-synced) isn't
// silently dropped; useRestTimer's web mount simply won't call Vibration.

const SOUND_KEY = 'jackedlogs.audio.v1.restSoundId';
const VIBRATE_KEY = 'jackedlogs.audio.v1.restVibrate';

const DEFAULT_VIBRATE = true;

let snapshot: { soundId: RestSoundId; vibrate: boolean } = {
  soundId: DEFAULT_REST_SOUND_ID,
  vibrate: DEFAULT_VIBRATE,
};

export const getRestAudioPreference = (): {
  soundId: RestSoundId;
  vibrate: boolean;
} => snapshot;

export const loadRestAudioPreference = async (): Promise<{
  soundId: RestSoundId;
  vibrate: boolean;
}> => {
  try {
    const soundRaw = localStorage.getItem(SOUND_KEY);
    const vibrateRaw = localStorage.getItem(VIBRATE_KEY);
    const soundId: RestSoundId = isRestSoundId(soundRaw)
      ? soundRaw
      : DEFAULT_REST_SOUND_ID;
    const vibrate = vibrateRaw === null ? DEFAULT_VIBRATE : vibrateRaw === '1';
    snapshot = { soundId, vibrate };
    return snapshot;
  } catch {
    return snapshot;
  }
};

const persistRestSoundId = async (soundId: RestSoundId): Promise<void> => {
  snapshot = { ...snapshot, soundId };
  try {
    localStorage.setItem(SOUND_KEY, soundId);
  } catch {
    // Snapshot already updated; persistence will retry on next save.
  }
};

const persistRestVibrate = async (vibrate: boolean): Promise<void> => {
  snapshot = { ...snapshot, vibrate };
  try {
    localStorage.setItem(VIBRATE_KEY, vibrate ? '1' : '0');
  } catch {
    // Same fallback rationale as persistRestSoundId.
  }
};

export function useRestAudioPreference(): {
  soundId: RestSoundId;
  vibrate: boolean;
  setSoundId: (id: RestSoundId) => void;
  setVibrate: (v: boolean) => void;
} {
  const [state, setState] = useState(snapshot);

  useEffect(() => {
    let cancelled = false;
    void loadRestAudioPreference().then((next) => {
      if (!cancelled) setState(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    soundId: state.soundId,
    vibrate: state.vibrate,
    setSoundId: (id) => {
      setState((s) => ({ ...s, soundId: id }));
      void persistRestSoundId(id);
    },
    setVibrate: (v) => {
      setState((s) => ({ ...s, vibrate: v }));
      void persistRestVibrate(v);
    },
  };
}
